import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {View, StyleSheet, FlatList, TouchableOpacity} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Divider,
  Chip,
  Checkbox,
  Button,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useAuthStore} from '../../store/useAuthStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {useSubscriptionStore} from '../../store/useSubscriptionStore';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const TAB_PLAN = 'plan';
const TAB_PAYMENTS = 'payments';

const MyAccountScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {user} = useAuthStore();
  const {orgUnits, fetchOrgUnits} = useOrgUnitStore();
  const {invoices, fetchInvoices} = useSubscriptionStore();

  const [activeTab, setActiveTab] = useState(TAB_PLAN);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchOrgUnits(), fetchInvoices()]);
      setLoading(false);
    };
    load();
  }, [fetchOrgUnits, fetchInvoices]);

  // Re-fetch on focus (e.g., returning from bulk renewal)
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchOrgUnits();
      fetchInvoices();
      setSelectedIds(new Set());
    });
    return unsubscribe;
  }, [navigation, fetchOrgUnits, fetchInvoices]);

  const tenant = user?.tenant;
  const memberSince = tenant?.createdAt
    ? new Date(tenant.createdAt).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  // Expired/expiring stores
  const expiredStores = useMemo(
    () =>
      (orgUnits || []).filter(
        ou =>
          ou.subscriptionStatus === 'EXPIRED' ||
          ou.subscriptionStatus === 'SUSPENDED',
      ),
    [orgUnits],
  );

  // Group org units by plan name
  const planGroups = useMemo(() => {
    const groups = {};
    (orgUnits || []).forEach(ou => {
      const planKey = ou.planName || t('myAccount.noPlan');
      if (!groups[planKey]) {
        groups[planKey] = [];
      }
      groups[planKey].push(ou);
    });
    return Object.entries(groups).map(([planName, stores]) => ({
      planName,
      stores,
    }));
  }, [orgUnits, t]);

  const toggleSelection = useCallback(storeId => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) {
        next.delete(storeId);
      } else {
        next.add(storeId);
      }
      return next;
    });
  }, []);

  const selectAllExpired = useCallback(() => {
    setSelectedIds(new Set(expiredStores.map(s => s.id)));
  }, [expiredStores]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBulkRenew = useCallback(() => {
    const selected = (orgUnits || []).filter(ou => selectedIds.has(ou.id));
    if (selected.length === 0) return;
    navigation.navigate('BulkRenewal', {selectedStores: selected});
  }, [selectedIds, orgUnits, navigation]);

  const handleSingleRenew = useCallback(
    store => {
      // Single store renewal — navigate to BulkRenewal with just one store
      navigation.navigate('BulkRenewal', {selectedStores: [store]});
    },
    [navigation],
  );

  const formatDate = useCallback(dateStr => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const getStatusColor = useCallback(status => {
    switch (status) {
      case 'ACTIVE':
      case 'TRIAL':
        return colors.success;
      case 'EXPIRED':
      case 'SUSPENDED':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  }, []);

  const getInvoiceStatusColor = useCallback(status => {
    switch (status) {
      case 'PAID':
        return colors.success;
      case 'ISSUED':
        return colors.info;
      case 'OVERDUE':
        return colors.error;
      case 'VOID':
        return colors.textSecondary;
      default:
        return colors.warning;
    }
  }, []);

  const isExpired = useCallback(
    status => status === 'EXPIRED' || status === 'SUSPENDED',
    [],
  );

  const renderHeader = useCallback(
    () => (
      <View>
        {/* Tenant info card */}
        <View style={styles.profileCard}>
          <Text style={styles.tenantName}>{tenant?.name || ''}</Text>
          <Text style={styles.memberSince}>
            {t('myAccount.memberSince')} {memberSince}
          </Text>
          {tenant?.ownerPhone ? (
            <View style={styles.phoneRow}>
              <MaterialCommunityIcons
                name="phone"
                size={16}
                color={colors.textSecondary}
              />
              <Text style={styles.phoneText}>{tenant.ownerPhone}</Text>
            </View>
          ) : null}
        </View>

        {/* Tabs */}
        <View style={styles.tabRow}>
          <TouchableOpacity
            style={[styles.tab, activeTab === TAB_PLAN && styles.tabActive]}
            onPress={() => setActiveTab(TAB_PLAN)}>
            <Text
              style={[
                styles.tabText,
                activeTab === TAB_PLAN && styles.tabTextActive,
              ]}>
              {t('myAccount.myPlan')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === TAB_PAYMENTS && styles.tabActive,
            ]}
            onPress={() => setActiveTab(TAB_PAYMENTS)}>
            <Text
              style={[
                styles.tabText,
                activeTab === TAB_PAYMENTS && styles.tabTextActive,
              ]}>
              {t('myAccount.payments')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    ),
    [tenant, memberSince, activeTab, t],
  );

  const renderPlanGroup = useCallback(
    ({item}) => (
      <View style={styles.planCard}>
        <View style={styles.planHeader}>
          <MaterialCommunityIcons
            name="shield-star"
            size={20}
            color={colors.primary}
          />
          <Text style={styles.planName}>{item.planName}</Text>
        </View>
        <Text style={styles.storesLabel}>
          {t('myAccount.stores')} ({item.stores.length})
        </Text>
        {item.stores.map(store => {
          const expired = isExpired(store.subscriptionStatus);
          const selected = selectedIds.has(store.id);

          return (
            <View key={store.id} style={styles.storeRow}>
              {/* Checkbox for expired stores */}
              {expired && (
                <Checkbox.Android
                  status={selected ? 'checked' : 'unchecked'}
                  onPress={() => toggleSelection(store.id)}
                  color={colors.primary}
                  uncheckedColor={colors.textSecondary}
                  style={styles.checkbox}
                />
              )}

              <View style={[styles.storeInfo, !expired && {marginLeft: 0}]}>
                <Text style={styles.storeName} numberOfLines={1}>
                  {store.name}
                </Text>
                {store.planExpiryDate ? (
                  <Text
                    style={[
                      styles.expiryText,
                      {color: getStatusColor(store.subscriptionStatus)},
                    ]}>
                    {expired
                      ? t('myAccount.expired')
                      : t('myAccount.expires') +
                        ': ' +
                        formatDate(store.planExpiryDate)}
                  </Text>
                ) : null}
              </View>

              <View style={styles.storeActions}>
                {expired && (
                  <TouchableOpacity
                    style={styles.renewLink}
                    onPress={() => handleSingleRenew(store)}>
                    <Text style={styles.renewText}>
                      {t('myAccount.renew')}
                    </Text>
                  </TouchableOpacity>
                )}
                <Chip
                  style={[
                    styles.statusChip,
                    {
                      backgroundColor:
                        getStatusColor(store.subscriptionStatus) + '18',
                    },
                  ]}
                  textStyle={[
                    styles.statusChipText,
                    {color: getStatusColor(store.subscriptionStatus)},
                  ]}
                  compact>
                  <Text style={[styles.statusChipText, {marginTop: 2, fontSize:10, color: getStatusColor(store.subscriptionStatus)}]}>{store.subscriptionStatus || t('myAccount.noPlan')}</Text>
                </Chip>
              </View>
            </View>
          );
        })}
      </View>
    ),
    [
      t,
      formatDate,
      getStatusColor,
      isExpired,
      selectedIds,
      toggleSelection,
      handleSingleRenew,
    ],
  );

  const renderInvoice = useCallback(
    ({item}) => (
      <TouchableOpacity
        style={styles.invoiceRow}
        onPress={() =>
          navigation.navigate('InvoiceDetail', {invoiceId: item.id})
        }
        activeOpacity={0.7}>
        <View style={styles.invoiceLeft}>
          <Text style={styles.invoiceNo}>{item.invoiceNo}</Text>
          <Text style={styles.invoiceDate}>
            {formatDate(item.invoiceDate)}
          </Text>
        </View>
        <View style={styles.invoiceRight}>
          <Text style={styles.invoiceAmount}>
            {'\u20B9'}
            {item.grandTotal != null
              ? Number(item.grandTotal).toFixed(2)
              : '0.00'}
          </Text>
          <Chip
            style={[
              styles.invoiceStatusChip,
              {
                backgroundColor:
                  getInvoiceStatusColor(item.invoiceStatus) + '18',
              },
            ]}
            textStyle={[
              styles.invoiceStatusChipText,
              {color: getInvoiceStatusColor(item.invoiceStatus)},
            ]}
            compact>
            <Text style={{marginTop: 2, fontSize:10}}>{item.invoiceStatus}</Text>
          </Chip>
        </View>
        <MaterialCommunityIcons
          name="chevron-right"
          size={20}
          color={colors.textSecondary}
        />
      </TouchableOpacity>
    ),
    [navigation, formatDate, getInvoiceStatusColor],
  );

  const renderPlanTab = useCallback(
    () => (
      <View>
        <Text style={styles.sectionTitle}>{t('myAccount.currentPlans')}</Text>

        {/* Bulk actions bar — shown when expired stores exist */}
        {expiredStores.length > 0 && (
          <View style={styles.bulkActionsBar}>
            <TouchableOpacity
              style={styles.selectAllBtn}
              onPress={
                selectedIds.size === expiredStores.length
                  ? clearSelection
                  : selectAllExpired
              }>
              <Checkbox.Android
                status={
                  selectedIds.size === expiredStores.length
                    ? 'checked'
                    : selectedIds.size > 0
                    ? 'indeterminate'
                    : 'unchecked'
                }
                color={colors.primary}
                onPress={
                  selectedIds.size === expiredStores.length
                    ? clearSelection
                    : selectAllExpired
                }
              />
              <Text style={styles.selectAllText}>
                {t('myAccount.selectAll')} ({expiredStores.length})
              </Text>
            </TouchableOpacity>

            {selectedIds.size === 0 && (
              <TouchableOpacity
                style={styles.renewAllBtn}
                onPress={() => {
                  selectAllExpired();
                  // Small delay to let state update, then navigate
                  setTimeout(() => {
                    navigation.navigate('BulkRenewal', {
                      selectedStores: expiredStores,
                    });
                  }, 100);
                }}>
                <MaterialCommunityIcons
                  name="lightning-bolt"
                  size={16}
                  color={colors.white}
                />
                <Text style={styles.renewAllText}>
                  {t('myAccount.renewAll')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {planGroups.length === 0 ? (
          <Text style={styles.emptyText}>{t('myAccount.noPlans')}</Text>
        ) : (
          planGroups.map((group, idx) => (
            <View key={idx}>{renderPlanGroup({item: group})}</View>
          ))
        )}
      </View>
    ),
    [
      planGroups,
      renderPlanGroup,
      expiredStores,
      selectedIds,
      selectAllExpired,
      clearSelection,
      navigation,
      t,
    ],
  );

  const renderPaymentsTab = useCallback(
    () => (
      <View>
        <Text style={styles.sectionTitle}>{t('myAccount.invoices')}</Text>
        {!invoices || invoices.length === 0 ? (
          <Text style={styles.emptyText}>{t('myAccount.noInvoices')}</Text>
        ) : (
          invoices.map(inv => (
            <View key={inv.id}>
              {renderInvoice({item: inv})}
              <Divider />
            </View>
          ))
        )}
      </View>
    ),
    [invoices, renderInvoice, t],
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action
          icon="menu"
          onPress={() => navigation.dispatch(DrawerActions.openDrawer())}
          color={colors.white}
        />
        <Appbar.Content
          title={t('myAccount.title')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <FlatList
        data={[]}
        renderItem={null}
        ListHeaderComponent={
          <>
            {renderHeader()}
            <View style={styles.content}>
              {activeTab === TAB_PLAN ? renderPlanTab() : renderPaymentsTab()}
            </View>
          </>
        }
      />

      {/* Floating bottom bar when stores are selected */}
      {selectedIds.size > 0 && (
        <View style={styles.selectionBar}>
          <Text style={styles.selectionCount}>
            {t('myAccount.renewSelected', {count: selectedIds.size})}
          </Text>
          <Button
            mode="contained"
            onPress={handleBulkRenew}
            buttonColor={colors.primary}
            compact
            style={styles.renewSelectedBtn}>
            {t('myAccount.renew')}
          </Button>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  content: {padding: spacing.md},

  // Profile card
  profileCard: {
    backgroundColor: colors.surface,
    margin: spacing.md,
    marginBottom: spacing.sm,
    borderRadius: 12,
    padding: spacing.lg,
    alignItems: 'center',
    elevation: 2,
    shadowColor: colors.black,
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tenantName: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4,
  },
  memberSince: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 6,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  phoneText: {
    fontSize: 14,
    color: colors.textSecondary,
  },

  // Tabs
  tabRow: {
    flexDirection: 'row',
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  tabActive: {
    borderColor: colors.text,
    backgroundColor: colors.text,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.textSecondary,
    letterSpacing: 0.5,
  },
  tabTextActive: {
    color: colors.white,
  },

  // Section
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },

  // Bulk actions bar
  bulkActionsBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    marginBottom: spacing.sm,
  },
  selectAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectAllText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
  },
  renewAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.error,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  renewAllText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.white,
  },

  // Plan card
  planCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  planHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  planName: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
  },
  storesLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 6,
  },
  storeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  checkbox: {
    marginRight: 4,
  },
  storeInfo: {flex: 1, marginRight: spacing.sm},
  storeName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  expiryText: {
    fontSize: 12,
    marginTop: 2,
  },
  storeActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  renewLink: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: colors.error + '12',
  },
  renewText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.error,
  },
  statusChip: {height: 26, marginTop:0},
  statusChipText: {fontSize: 11, fontWeight: '600'},

  // Selection bar (floating bottom)
  selectionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    elevation: 8,
    shadowColor: colors.black,
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.15,
    shadowRadius: 8,
  },
  selectionCount: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  renewSelectedBtn: {
    borderRadius: 20,
    paddingHorizontal: 20,
  },

  // Invoice row
  invoiceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    marginBottom: 1,
  },
  invoiceLeft: {flex: 1},
  invoiceNo: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  invoiceDate: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  invoiceRight: {
    alignItems: 'flex-end',
    marginRight: spacing.sm,
  },
  invoiceAmount: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  invoiceStatusChip: {height: 26, marginTop: 4},
  invoiceStatusChipText: {fontSize: 10, fontWeight: '600'},

  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    paddingVertical: spacing.xl,
  },
});

export default MyAccountScreen;
